// src/App.js
import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./app/store";
import { Toaster } from "react-hot-toast";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import MaintenancePage from "./components/MaintenancePage";
import PageLoader from "./components/PageLoader";

// Lazy-loaded Pages & Components
const Home = lazy(() => import("./pages/home"));
const Login = lazy(() => import("./pages/login"));
const Signup = lazy(() => import("./pages/signup"));
const CourseCreationPage = lazy(() => import("./pages/addcourse"));
const AllCourses = lazy(() => import("./components/allcourses"));
const ViewOneCourse = lazy(() => import("./pages/viewonecourse"));
const Quiz = lazy(() => import("./pages/quiz"));
const ExtendedView = lazy(() => import("./pages/extendedview"));
const EditProfile = lazy(() => import("./pages/editprofile"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const InstructorDashboard = lazy(() => import("./pages/instructor/InstructorDashboard"));
const CohortDetail = lazy(() => import("./components/instructor/CohortDetail"));
const CohortGroups = lazy(() => import("./components/instructor/CohortGroups"));
const UploadMarks = lazy(() => import("./components/instructor/UploadMarks"));
const AssignmentDetails = lazy(() => import("./components/instructor/AssignmentDetails"));
const AssignAssignment = lazy(() => import("./components/instructor/AssignAssignment"));
const AssignmentCommunity = lazy(() => import("./components/instructor/AssignmentCommunity"));
const InstructorCohortGrading = lazy(() => import("./components/instructor/InstructorCohortGrading"));
const InstructorCohortGradingDetail = lazy(() => import("./components/instructor/InstructorCohortGradingDetail"));
const StudentDashboard = lazy(() => import("./pages/student/StudentDashboard"));
const Leaderboard = lazy(() => import("./pages/student/Leaderboard"));
const LeaderboardForNavbar = lazy(() => import("./pages/leaderboardfornavbar"));
const StudentCohortDetail = lazy(() => import("./pages/cohort/student/StudentCohortDetail"));
const StudentAssignments = lazy(() => import("./pages/cohort/student/StudentAssignments"));
const StudentAssignmentCommunity = lazy(() => import("./components/cohort/student/AssignmentCommunity"));
const ReviewCourse = lazy(() => import("./pages/ReviewCourse"));
const EditCoursePage = lazy(() => import("./pages/editcourse/EditCoursePage"));
const DemoPage = lazy(() => import("./pages/editcourse/DemoPage"));
const LiveClassDetail = lazy(() => import("./pages/student/LiveClassDetail"));
const InstructorLiveClassDetail = lazy(() => import("./components/instructor/InstructorLiveClassDetail"));
const About = lazy(() => import("./pages/about"));
const CourseResults = lazy(() => import("./pages/CourseResults"));
const CoursesCategory = lazy(() => import("./pages/CoursesCategory"));
const Explore = lazy(() => import("./pages/explore"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const UserConductPolicy = lazy(() => import("./pages/UserConductPolicy"));
const AcademicIntegrityPolicy = lazy(() => import("./pages/AcademicIntegrityPolicy"));
const ContentUsageCopyrightPolicy = lazy(() => import("./pages/ContentUsageCopyrightPolicy"));
const CertificationAssessmentPolicy = lazy(() => import("./pages/CertificationAssessmentPolicy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Documentation = lazy(() => import("./pages/Documentation"));
const CEOMessage = lazy(() => import("./pages/CEOMessage"));
const Donation = lazy(() => import("./pages/Donation"));
const Subscription = lazy(() => import("./pages/Subscription"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));

// Community Components
const CommunityHub = lazy(() => import("./pages/community/CommunityHub"));
const CommunityFeed = lazy(() => import("./pages/community/CommunityFeed"));
const MyPosts = lazy(() => import("./pages/community/MyPosts"));
const CreatePost = lazy(() => import("./pages/community/CreatePost"));

// Exam Components
const MakeExam = lazy(() => import("./pages/exam/MakeExam"));
const TakeExam = lazy(() => import("./pages/exam/TakeExam"));
const ExamSubmissions = lazy(() => import("./pages/exam/ExamSubmissions"));

// Blog Components
const BlogHub = lazy(() => import("./pages/blog/BlogHub"));
const BlogDetail = lazy(() => import("./pages/blog/BlogDetail"));
const CreateBlogPost = lazy(() => import("./pages/blog/CreatePost"));
const EditBlogPost = lazy(() => import("./pages/blog/EditPost"));

// Blog User View Components
const BlogHubUser = lazy(() => import("./pages/blog/blogUserView/BlogHubUser"));
const BlogDetailUser = lazy(() => import("./pages/blog/blogUserView/BlogDetailUser"));

// Floating Chatbot (loaded on demand)
const FloatingChatbotButton = lazy(() => import("./components/FloatingChatbotButton"));

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUsername = localStorage.getItem("username");
    const role = localStorage.getItem("role");

    if (token && storedUsername && role) {
      setIsLoggedIn(true);
    }
  }, []);

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  // Component to handle chatbot button visibility based on current route
  const ChatbotButtonWrapper = () => {
    const location = useLocation();
    const hideChatbotPaths = ['/login', '/signup', '/editprofile'];
    const shouldHideChatbot = hideChatbotPaths.includes(location.pathname);

    // Show chatbot button only if user is logged in AND not on login/signup/editprofile pages
    return isLoggedIn && !shouldHideChatbot ? (
      <Suspense fallback={null}>
        <FloatingChatbotButton />
      </Suspense>
    ) : null;
  };

  return (
    <Provider store={store}>
      <Router>
        <ScrollToTop />
        <Toaster position="top-right" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Default route - Home page accessible to all */}
            <Route path="/" element={<Home />} />

          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/about" element={<About />} />
          <Route path="/courses" element={<CourseResults />} />
          <Route path="/courses-category" element={<CoursesCategory />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/user-conduct-policy" element={<UserConductPolicy />} />
          <Route
            path="/academic-integrity-policy"
            element={<AcademicIntegrityPolicy />}
          />
          <Route
            path="/content-usage-copyright-policy"
            element={<ContentUsageCopyrightPolicy />}
          />
          <Route
            path="/certification-assessment-policy"
            element={<CertificationAssessmentPolicy />}
          />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/ceo-message" element={<CEOMessage />} />
          <Route path="/donation" element={<Donation />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          {/* Community Routes - No Authentication Required */}
          <Route path="/community" element={<CommunityHub />} />
          <Route path="/community/feed" element={<CommunityFeed />} />
          <Route path="/community/my-posts" element={<MyPosts />} />
          <Route path="/community/create" element={<CreatePost />} />

          {/* Blog Routes - Admin Only */}
          <Route path="/blog" element={<BlogHub />} />
          <Route path="/blog/create" element={<CreateBlogPost />} />
          <Route path="/blog/edit/:id" element={<EditBlogPost />} />
          <Route path="/blog/:id" element={<BlogDetail />} />

          {/* Blog Routes - Public User View (Students/Teachers) */}
          <Route path="/blogs" element={<BlogHubUser />} />
          <Route path="/blogs/:id" element={<BlogDetailUser />} />

          {/* Home route - also accessible for consistency */}
          <Route path="/home" element={<Home />} />
          <Route
            path="/addcourse"
            element={
              <ProtectedRoute>
                <CourseCreationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/allcourses"
            element={
              <ProtectedRoute>
                <AllCourses />
              </ProtectedRoute>
            }
          />
          <Route path="/viewonecourse" element={<ViewOneCourse />} />
          <Route
            path="/quiz"
            element={
              <ProtectedRoute>
                <Quiz />
              </ProtectedRoute>
            }
          />
          <Route
            path="/extendedview"
            element={
              <ProtectedRoute>
                <ExtendedView />
              </ProtectedRoute>
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="/admin"
            element={
              // <ProtectedRoute>
              <AdminDashboard />
              // </ProtectedRoute>
            }
          />

          {/* Admin Review Course */}
          <Route
            path="/review"
            element={
              // <ProtectedRoute>
              <ReviewCourse />
              // </ProtectedRoute>
            }
          />

          {/* Instructor Dashboard */}
          <Route
            path="/instructor"
            element={
              // <ProtectedRoute>
              <InstructorDashboard />
              // </ProtectedRoute>
            }
          />

          {/* Instructor Dashboard Alternative Route */}
          <Route
            path="/instructor-dashboard"
            element={
              // <ProtectedRoute>
              <InstructorDashboard />
              // </ProtectedRoute>
            }
          />

          {/* Cohort Detail Route */}
          <Route
            path="/instructor/cohort/:id"
            element={
              // <ProtectedRoute>
              <CohortDetail />
              // </ProtectedRoute>
            }
          />

          {/* Make Exam Route */}
          <Route
            path="/makeexam"
            element={
              // <ProtectedRoute>
              <MakeExam />
              // </ProtectedRoute>
            }
          />

          {/* Take Exam Route */}
          <Route
            path="/takeexam"
            element={
              // <ProtectedRoute>
              <TakeExam />
              // </ProtectedRoute>
            }
          />

          {/* Exam Submissions Route */}
          <Route
            path="/exam-submissions"
            element={
              // <ProtectedRoute>
              <ExamSubmissions />
              // </ProtectedRoute>
            }
          />

          {/* Cohort Sub-pages Routes */}
          <Route
            path="/instructor/cohort/:id/groups"
            element={
              // <ProtectedRoute>
              <CohortGroups />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/cohort/:id/upload-marks"
            element={
              // <ProtectedRoute>
              <UploadMarks />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/cohort/:id/assignment/:assignmentId"
            element={
              // <ProtectedRoute>
              <AssignmentDetails />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/cohort/:id/assign-assignment"
            element={
              // <ProtectedRoute>
              <AssignAssignment />
              // </ProtectedRoute>
            }
          />

          {/* Assignment Community Route */}
          <Route
            path="/assignment-community"
            element={
              // <ProtectedRoute>
              <AssignmentCommunity />
              // </ProtectedRoute>
            }
          />

          {/* Instructor Cohort Grading Routes */}
          <Route
            path="/instructor-cohort-grading"
            element={
              // <ProtectedRoute>
              <InstructorCohortGrading />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/instructor-cohort-grading-detail"
            element={
              // <ProtectedRoute>
              <InstructorCohortGradingDetail />
              // </ProtectedRoute>
            }
          />

          {/* Student Dashboard */}
          <Route
            path="/student"
            element={
              <ProtectedRoute>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Student Dashboard with specific section */}
          <Route
            path="/student/dashboard"
            element={
              <ProtectedRoute>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Student Dashboard Alternative Route */}
          <Route
            path="/student-dashboard"
            element={
              <ProtectedRoute>
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Live Class Detail Page */}
          <Route
            path="/live-class-detail/:courseId"
            element={
              <ProtectedRoute>
                <LiveClassDetail />
              </ProtectedRoute>
            }
          />

          {/* Instructor Live Class Detail Page */}
          <Route
            path="/instructor/live-class/:courseId"
            element={
              <ProtectedRoute>
                <InstructorLiveClassDetail />
              </ProtectedRoute>
            }
          />

          {/* Student Cohort Detail Page */}
          <Route
            path="/student/cohort/:cohortId"
            element={
              <ProtectedRoute>
                <StudentCohortDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/assignments/:lectureId"
            element={
              <ProtectedRoute>
                <StudentAssignments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/student/assignment/:assignmentId/community"
            element={
              <ProtectedRoute>
                <StudentAssignmentCommunity />
              </ProtectedRoute>
            }
          />

          {/* Leaderboard - Unprotected Route for Testing */}
          <Route path="/student/leaderboard" element={<Leaderboard />} />

          {/* Leaderboard for Navbar - Student Only */}
          <Route path="/leaderboard" element={<LeaderboardForNavbar />} />

          {/* Edit Profile */}
          <Route
            path="/editprofile"
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            }
          />

          {/* Edit Course Routes */}
          <Route
            path="/editcourse/:courseId"
            element={
              // <ProtectedRoute>
              <EditCoursePage />
              // </ProtectedRoute>
            }
          />
          <Route
            path="/editcourse/demo"
            element={
              <ProtectedRoute>
                <DemoPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
        {/* Floating Chatbot Button */}
        <ChatbotButtonWrapper />
      </Router>
    </Provider>
  );
};

export default App;
